"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { Popover } from "@base-ui/react/popover";
import react, { useEffect, useState } from "react";
import { PortalContainerProvider, usePortalContainer } from "../../contexts/index.js";
import { cn } from "../../utils/cn.js";
import { IconButton } from "../IconButton/index.js";
import ArrowSVG from "./ArrowSVG.js";
import { popoverVariants } from "./Popover.variants.js";
import { PopoverContent } from "./PopoverContent.js";
const nativeButtonComponentNames = new Set([
    'Button',
    'IconButton',
    'NonOpenableFilterChip'
]);
function getExplicitComponentDisplayName(type) {
    if ('string' == typeof type) return '';
    return type.displayName || '';
}
function getNativeButtonTriggerKind(triggerNode) {
    if (!/*#__PURE__*/ react.isValidElement(triggerNode)) return null;
    if ('button' === triggerNode.type) return 'element';
    if ('string' == typeof triggerNode.type) return null;
    const displayName = getExplicitComponentDisplayName(triggerNode.type);
    if (nativeButtonComponentNames.has(displayName)) return 'component';
    if ('FilterChip' === displayName && true !== triggerNode.props.opens) return 'component';
    return null;
}
function cloneNativeButtonTrigger(triggerElement, baseTriggerProps, triggerClasses, omitNativeType) {
    const propsForTrigger = {
        ...baseTriggerProps
    };
    if (omitNativeType) delete propsForTrigger.type;
    return /*#__PURE__*/ react.cloneElement(triggerElement, {
        ...triggerElement.props,
        ...propsForTrigger,
        className: cn(triggerClasses, baseTriggerProps.className, triggerElement.props.className),
        onClick: (e)=>{
            triggerElement.props.onClick?.(e);
            if (e.defaultPrevented) return void e.stopPropagation();
            baseTriggerProps.onClick?.(e);
        },
        onKeyDown: (e)=>{
            triggerElement.props.onKeyDown?.(e);
            if (e.defaultPrevented) return void e.stopPropagation();
            baseTriggerProps.onKeyDown?.(e);
        }
    });
}
function Popover_Popover({ open = false, onOpenChange, defaultOpen, openOnHover = true, headingCustomNode, headingIconName, headingRightNode, align = 'center', triggerNode, headingText, children, side = 'bottom', ariaLabel, description, footer, showArrow = true, slotClassNames = {}, onClose, hideCloseButton = false, positionerOffset = 8, disableTriggerFocus = false, modal = false, ...props }) {
    const triggerRef = react.useRef(null);
    const [isOpen, setIsOpen] = useState(open);
    const noHeading = !headingText && !headingCustomNode;
    const parentPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState(null);
    const { popupSlot, popupSlotAnimation, arrowSlot, headingSlot, headingCustomSlot, headingLeftSlot, headingRightSlot, headingCloseButtonSlot, headingTextSlot, headingIconSlot, backdropSlot, triggerSlot, bodySlot, bodyTextSlot, scrollAreaSlot, contentSlot, footerSlot, positionerSlot } = popoverVariants({
        customHeader: !!headingCustomNode,
        customFooter: !!footer,
        customBody: !!children,
        noHeading,
        ...props
    });
    const { triggerClass = '', popupClass = '', backdropClass = '', scrollAreaClass = '', bodyClass = '', footerClass = '', contentClass = '', arrowClass = '', headingClass = '', positionerClass = '' } = slotClassNames;
    const triggerClasses = cn(triggerSlot(), triggerClass);
    const popupClasses = cn(popupSlot(), popupSlotAnimation(), popupClass);
    const backdropClasses = cn(backdropSlot(), backdropClass);
    const scrollAreaClasses = cn(scrollAreaSlot(), scrollAreaClass);
    const bodyClasses = cn(bodySlot(), bodyClass);
    const footerClasses = cn(footerSlot(), footerClass);
    const contentClasses = cn(contentSlot(), contentClass);
    const arrowClasses = cn(arrowSlot(), arrowClass);
    const headingClasses = cn(headingSlot(), headingClass);
    const positionerClasses = cn(positionerSlot(), positionerClass);
    const renderCloseButton = ()=>/*#__PURE__*/ jsx(IconButton, {
            iconName: "close",
            type: "primary",
            shadow: true,
            size: "md",
            "aria-label": "Close popover",
            emphasis: "bold",
            onClick: (event)=>{
                event.stopPropagation();
                handleClose(event);
            }
        });
    function handleClose(event) {
        setIsOpen(false);
        onClose?.();
        onOpenChange?.(false, event?.nativeEvent);
    }
    function handleOpenChange(open, eventDetails) {
        setIsOpen(open);
        onOpenChange?.(open, eventDetails.event);
    }
    useEffect(()=>{
        setIsOpen(open);
    }, [
        open
    ]);
    const nativeButtonTriggerKind = getNativeButtonTriggerKind(triggerNode);
    const usesNativeButtonTrigger = null !== nativeButtonTriggerKind;
    return /*#__PURE__*/ jsxs(Popover.Root, {
        open: isOpen,
        defaultOpen: defaultOpen,
        onOpenChange: handleOpenChange,
        "data-testid": "root",
        modal: modal,
        children: [
            triggerNode ? /*#__PURE__*/ jsx(Popover.Trigger, {
                ref: triggerRef,
                "data-testid": "trigger",
                nativeButton: usesNativeButtonTrigger,
                openOnHover: openOnHover,
                render: (props)=>{
                    const baseTriggerProps = {
                        ...props,
                        onClick: (e)=>{
                            e.stopPropagation();
                            props.onClick?.(e);
                        },
                        onKeyDown: (e)=>{
                            e.stopPropagation();
                            props.onKeyDown?.(e);
                        },
                        ...disableTriggerFocus && {
                            tabIndex: -1
                        }
                    };
                    if (nativeButtonTriggerKind) {
                        const triggerElement = triggerNode;
                        return cloneNativeButtonTrigger(triggerElement, baseTriggerProps, triggerClass, 'component' === nativeButtonTriggerKind);
                    }
                    return /*#__PURE__*/ jsx("div", {
                        className: triggerClasses,
                        ...baseTriggerProps,
                        children: triggerNode
                    });
                }
            }) : null,
            /*#__PURE__*/ jsxs(Popover.Portal, {
                container: parentPortalContainer || void 0,
                children: [
                    /*#__PURE__*/ jsx(Popover.Backdrop, {
                        className: backdropClasses
                    }),
                    /*#__PURE__*/ jsx(Popover.Positioner, {
                        sideOffset: positionerOffset,
                        align: align,
                        side: side,
                        className: positionerClasses,
                        children: /*#__PURE__*/ jsxs(Popover.Popup, {
                            finalFocus: triggerRef,
                            className: popupClasses,
                            "aria-label": ariaLabel,
                            children: [
                                showArrow ? /*#__PURE__*/ jsx(Popover.Arrow, {
                                    "data-testid": "arrowSvg",
                                    className: arrowClasses,
                                    children: /*#__PURE__*/ jsx(ArrowSVG, {
                                        ...props
                                    })
                                }) : null,
                                /*#__PURE__*/ jsx(PortalContainerProvider, {
                                    container: portalContainer,
                                    children: /*#__PURE__*/ jsxs("div", {
                                        ref: setPortalContainer,
                                        className: "contents",
                                        children: [
                                            /*#__PURE__*/ jsx(PopoverContent, {
                                                headingCustomSlot: headingCustomSlot(),
                                                headingCloseButtonSlot: headingCloseButtonSlot(),
                                                headingLeftSlot: headingLeftSlot(),
                                                headingRightSlot: headingRightSlot(),
                                                headingTextSlot: headingTextSlot(),
                                                headingIconSlot: headingIconSlot(),
                                                bodyTextSlot: bodyTextSlot(),
                                                renderCloseButton: renderCloseButton,
                                                headingCustomNode: headingCustomNode,
                                                headingText: headingText,
                                                headingIconName: headingIconName,
                                                headingRightNode: headingRightNode,
                                                description: description,
                                                footer: footer,
                                                headingClasses: headingClasses,
                                                scrollAreaClasses: scrollAreaClasses,
                                                contentClasses: contentClasses,
                                                bodyClasses: bodyClasses,
                                                footerClasses: footerClasses,
                                                children: children
                                            }),
                                            noHeading && !hideCloseButton ? /*#__PURE__*/ jsx("div", {
                                                "data-testid": "noHeadingCloseButton",
                                                className: headingCloseButtonSlot(),
                                                children: /*#__PURE__*/ jsx(Popover.Close, {
                                                    render: ()=>/*#__PURE__*/ jsx("div", {
                                                            children: renderCloseButton()
                                                        })
                                                })
                                            }) : null
                                        ]
                                    })
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
const components_Popover_Popover = Popover_Popover;
export { Popover_Popover as Popover, components_Popover_Popover as default };
